import { SupportedLocale } from "@/i18n";
import { useTranslation } from "react-i18next";
import * as AF from "../form-items/AbstractForm";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import useImageAttachment from "@/hooks/useImageAttachment";
import { useMemo } from "react";
import { useAuth } from "@/context/auth/AuthContext";
import { generateImageName } from "@/lib/utils";

export interface AboutUsFormProps {
  language: SupportedLocale;
}

export const AboutUsForm = ({ language }: AboutUsFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const imagePosition = useMemo(
    () => `about${language.toLocaleUpperCase()}`,
    [language],
  );

  const { attachment, imageURL, refetch } = useImageAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.pageName`}
        label={t("cms.form.about-us.pageName")}
      />

      <CroppedImageInput
        imageURL={imageURL}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={1.27}
        title={t("addImage")}
        buttonLabel={t("changePhoto")}
        label={t("cms.form.about-us.image")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.about-us"),
          })
        }
      />

      <AF.Text
        name={`${language}.credits`}
        label={t("cms.about-us.form.credits")}
        tootltip={t("cms.about-us.form.credits.tooltip")}
      />

      <AF.RichText
        name={`${language}.content`}
        label={t("cms.form.about-us.content")}
      />
    </AF.Col>
  );
};

export const AboutUsCreditForm = ({ language }: AboutUsFormProps) => {
  const { t } = useTranslation();
  return (
    <AF.Col>
      <AF.Text
        name={`${language}.creditsTitle`}
        label={t("cms.form.about-us.creditsTitle")}
      />
      <AF.RichText
        name={`${language}.creditsContent`}
        label={t("cms.form.about-us.creditsContent")}
      />
    </AF.Col>
  );
};

export const AboutUsLegalNoticesForm = ({ language }: AboutUsFormProps) => {
  const { t } = useTranslation();
  return (
    <AF.Col>
      <AF.Text
        name={`${language}.legalNoticesTitle`}
        label={t("cms.form.about-us.legalNoticesTitle")}
      />
      <AF.RichText
        name={`${language}.legalNoticesContent`}
        label={t("cms.form.about-us.legalNoticesContent")}
      />
    </AF.Col>
  );
};
